import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fulfillmentsApi } from '../../lib/api';
import OwnerConfirmationModal from './OwnerConfirmationModal';
import Modal from '../Modal';

interface PendingConfirmation {
  id: string;
  rentalId: string;
  rentalIsPaid: boolean;
  itemTitle: string;
  renterName: string;
  renterAvatar?: string;
  pickupAddress: string;
  pickupWindowStart: string;
  pickupWindowEnd?: string;
  requestedAt: string;
}

export default function PendingConfirmationsWidget() {
  const navigate = useNavigate();
  const [confirmations, setConfirmations] = useState<PendingConfirmation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConfirmation, setSelectedConfirmation] = useState<PendingConfirmation | null>(null);
  const [suggestConfirmation, setSuggestConfirmation] = useState<PendingConfirmation | null>(null);
  const [suggestStart, setSuggestStart] = useState('');
  const [suggestEnd, setSuggestEnd] = useState('');
  const [suggestMessage, setSuggestMessage] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingConfirmations();
  }, []);

  const fetchPendingConfirmations = async () => {
    try {
      setLoading(true);
      const response = await fulfillmentsApi.getPendingConfirmations();
      if (response.data.success) {
        setConfirmations(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching pending confirmations:', err);
      setError(err?.response?.data?.error || 'Could not load pending confirmations');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmed = (fulfillmentId: string) => {
    setConfirmations(prev => prev.filter(c => c.id !== fulfillmentId));
    setSelectedConfirmation(null);
  };

  const handleSuggestTime = async () => {
    if (!suggestConfirmation || !suggestStart || !suggestEnd) return;
    setSuggesting(true);
    setSuggestError(null);
    try {
      await fulfillmentsApi.suggestTime(suggestConfirmation.id, {
        proposedWindowStart: new Date(suggestStart).toISOString(),
        proposedWindowEnd: new Date(suggestEnd).toISOString(),
        message: suggestMessage || undefined,
      });
      setSuggestConfirmation(null);
      setSuggestStart('');
      setSuggestEnd('');
      setSuggestMessage('');
    } catch (err: any) {
      setSuggestError(err?.response?.data?.error || 'Failed to send suggestion');
    } finally {
      setSuggesting(false);
    }
  };

  const openSuggestModal = (confirmation: PendingConfirmation) => {
    const existingStart = confirmation.pickupWindowStart ? new Date(confirmation.pickupWindowStart) : new Date();
    const existingEnd = confirmation.pickupWindowEnd ? new Date(confirmation.pickupWindowEnd) : new Date(Date.now() + 60 * 60 * 1000);
    setSuggestStart(existingStart.toISOString().slice(0, 16));
    setSuggestEnd(existingEnd.toISOString().slice(0, 16));
    setSuggestMessage('');
    setSuggestError(null);
    setSuggestConfirmation(confirmation);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Pending Confirmations</h3>
        <div className="text-[var(--text-secondary)]">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-[var(--border)] p-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Pending Confirmations</h3>
        <div className="text-red-600 text-sm">{error}</div>
      </div>
    );
  }

  if (confirmations.length === 0) {
    return null; // Don't show if no pending confirmations
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden mb-6">
        <div className="p-6 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <i className="mdi mdi-alert-circle text-amber-500 text-xl"></i>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
              Pending Confirmations ({confirmations.length})
            </h3>
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            These renters are waiting for you to confirm pickup details
          </p>
        </div>

        <div className="divide-y divide-[var(--border)]">
          {confirmations.map((confirmation) => (
            <div
              key={confirmation.id}
              onClick={() => navigate(`/rental/${confirmation.rentalId}`)}
              className="p-4 hover:bg-[var(--bg-tint)] transition-colors duration-200 cursor-pointer"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[var(--text-primary)]">
                      {confirmation.renterName}
                    </span>
                    <span className="text-[var(--text-secondary)]">wants to pick up</span>
                    <span className="font-semibold text-[var(--text-primary)]">
                      {confirmation.itemTitle}
                    </span>
                  </div>
                  
                  <div className="text-sm text-[var(--text-secondary)] space-y-1">
                    <div className="flex items-center gap-2">
                      <i className="mdi mdi-map-marker text-[var(--forest-green)]"></i>
                      <span>{confirmation.pickupAddress}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="mdi mdi-calendar-clock text-[var(--forest-green)]"></i>
                      <span>
                        {new Date(confirmation.pickupWindowStart).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                        {' '}
                        {new Date(confirmation.pickupWindowStart).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                        {confirmation.pickupWindowEnd && (
                          <>
                            {' - '}
                            {new Date(confirmation.pickupWindowEnd).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-[var(--text-muted)] mt-2">
                    Requested {new Date(confirmation.requestedAt).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {confirmation.rentalIsPaid ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedConfirmation(confirmation);
                      }}
                      className="btn btn-primary whitespace-nowrap"
                    >
                      <i className="mdi mdi-check mr-1"></i>
                      Confirm
                    </button>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 whitespace-nowrap">
                      Awaiting payment from renter
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openSuggestModal(confirmation);
                    }}
                    className="btn btn-secondary whitespace-nowrap"
                  >
                    <i className="mdi mdi-calendar-edit mr-1"></i>
                    Suggest Time
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedConfirmation && (
        <OwnerConfirmationModal
          isOpen={!!selectedConfirmation}
          onClose={() => setSelectedConfirmation(null)}
          onConfirmed={() => handleConfirmed(selectedConfirmation.id)}
          fulfillmentId={selectedConfirmation.id}
          rentalId={selectedConfirmation.rentalId}
          itemTitle={selectedConfirmation.itemTitle}
          renterName={selectedConfirmation.renterName}
          pickupAddress={selectedConfirmation.pickupAddress}
          initialPickupDate={selectedConfirmation.pickupWindowStart}
        />
      )}

      {suggestConfirmation && (
        <Modal
          isOpen={!!suggestConfirmation}
          title={`Suggest a different time for "${suggestConfirmation.itemTitle}"`}
          confirmLabel={suggesting ? 'Sending...' : 'Send suggestion'}
          cancelLabel="Cancel"
          onConfirm={handleSuggestTime}
          onCancel={() => setSuggestConfirmation(null)}
          confirmVariant="primary"
        >
          <div className="space-y-4">
            <p className="text-sm text-[var(--text-secondary)]">
              Propose an alternative pickup window for {suggestConfirmation.renterName}.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Proposed Start</label>
                <input
                  type="datetime-local"
                  value={suggestStart}
                  onChange={(e) => setSuggestStart(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Proposed End</label>
                <input
                  type="datetime-local"
                  value={suggestEnd}
                  onChange={(e) => setSuggestEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Message (optional)</label>
              <textarea
                value={suggestMessage}
                onChange={(e) => setSuggestMessage(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg resize-vertical"
                placeholder="e.g. The morning works better for me"
              />
            </div>
            {suggestError && (
              <p className="text-sm text-red-600">{suggestError}</p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
